import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { IconButton } from '@/components/ui';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Iframe } from '@/components/ui/Iframe';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface DebugDrawerProps {
  chatId: string;
}

type DebugType = 'sys' | 'ctx';

const DebugTabContent = ({
  type,
  chatId,
  isOpen,
}: {
  type: DebugType;
  chatId: string;
  isOpen: boolean;
}) => {
  const {
    data: htmlContent,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['debug', 'chat', chatId, type],
    queryFn: () => backendApis.debug.getChatDebugHtml(chatId, type),
    enabled: isOpen,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icons.loader className="size-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-danger-9 p-4 text-center">
        <Icons.warn className="mx-auto mb-2 size-6" />
        <p>Failed to load debug information</p>
      </div>
    );
  }

  if (!htmlContent) {
    return null;
  }

  return (
    <Iframe
      srcDoc={htmlContent}
      title={`Debug ${type}`}
      className="h-full w-full border-0"
      style={{ minHeight: '80vh' }}
      sandbox="allow-same-origin"
    />
  );
};

export const DebugDrawer = ({ chatId }: DebugDrawerProps) => {
  const [activeTab, setActiveTab] = useState<DebugType>('ctx');

  const sheetName = 'debug';
  const { openSheet } = BottomSheet.useBottomSheet();
  const isOpen = openSheet === sheetName;

  useEffect(() => {
    setActiveTab('ctx');
  }, [chatId]);

  return (
    <BottomSheet.Sheet
      name={sheetName}
      ariaLabel="Debug Chat"
      className="max-h-[95vh]"
    >
      <BottomSheet.Header>
        <div className="flex items-center justify-between">
          <BottomSheet.Title>Debug Chat</BottomSheet.Title>
          <BottomSheet.Close asChild>
            <IconButton size="sm" icon={Icons.cancel} />
          </BottomSheet.Close>
        </div>
      </BottomSheet.Header>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as DebugType)}
        className="flex flex-1 flex-col gap-0"
      >
        <div className="px-4 pt-2">
          <TabsList>
            <TabsTrigger value="ctx">Context (CTX)</TabsTrigger>
            <TabsTrigger value="sys">System (SYS)</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="ctx" className="flex-1 overflow-auto p-4">
          <DebugTabContent type="ctx" chatId={chatId} isOpen={isOpen} />
        </TabsContent>

        <TabsContent value="sys" className="flex-1 overflow-auto p-4">
          <DebugTabContent type="sys" chatId={chatId} isOpen={isOpen} />
        </TabsContent>
      </Tabs>
    </BottomSheet.Sheet>
  );
};
